import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { backofficeApi, formatCurrency, formatDate } from '@kardume/services';
import type { SubscriptionDto, SubscriptionStatus, BillingPeriod } from '@kardume/types';
import { PageHeader, SearchInput, Table, TableContainer, Badge } from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const Muted = styled.span`
  color: ${(p) => p.theme.textMuted};
`;

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  ACTIVE: 'Ativa',
  TRIALING: 'Em teste',
  PAST_DUE: 'Em atraso',
  CANCELED: 'Cancelada',
};

const BILLING_LABELS: Record<BillingPeriod, string> = {
  MONTHLY: '/mês',
  QUARTERLY: '/trimestre',
  YEARLY: '/ano',
};

/** Map a subscription status to a Badge tone. */
function statusTone(status: SubscriptionStatus): 'success' | 'warning' | 'danger' {
  if (status === 'ACTIVE') return 'success';
  if (status === 'TRIALING' || status === 'PAST_DUE') return 'warning';
  return 'danger';
}

/** Backoffice screen listing every merchant subscription on the platform. */
const SubscriptionsPage: React.FC = () => {
  const [search, setSearch] = useState<string>('');
  const { data, loading, error, reload } = useAsync(
    () => backofficeApi.listSubscriptions({ size: 100 }),
    []
  );

  const subscriptions = useMemo<SubscriptionDto[]>(() => {
    const list = data?.content ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((s) => {
      const merchant = (s.merchantName ?? '').toLowerCase();
      const plan = (s.planName ?? '').toLowerCase();
      return merchant.includes(term) || plan.includes(term);
    });
  }, [data, search]);

  return (
    <>
      <PageHeader
        title="Assinaturas"
        subtitle="Todas as assinaturas dos lojistas no Kardume Urbano."
      />

      <Toolbar>
        <SearchInput
          placeholder="Buscar por lojista ou plano..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        onRetry={reload}
        isEmpty={subscriptions.length === 0}
        emptyTitle="Nenhuma assinatura encontrada"
        emptyDescription="Assim que os lojistas assinarem um plano, as assinaturas aparecerão aqui."
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Lojista</th>
                <th>Plano</th>
                <th>Status</th>
                <th>Valor</th>
                <th>Fim do período</th>
                <th>Próxima fatura</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((subscription) => (
                <tr key={subscription.id}>
                  <td>
                    <strong>{subscription.merchantName ?? '-'}</strong>
                  </td>
                  <td>{subscription.planName ?? <Muted>-</Muted>}</td>
                  <td>
                    <Badge tone={statusTone(subscription.status)}>
                      {STATUS_LABELS[subscription.status]}
                    </Badge>
                  </td>
                  <td>
                    {formatCurrency(subscription.price)}
                    <Muted>{BILLING_LABELS[subscription.billingPeriod]}</Muted>
                  </td>
                  <td>{formatDate(subscription.currentPeriodEnd)}</td>
                  <td>
                    {subscription.nextInvoiceAt ? (
                      formatDate(subscription.nextInvoiceAt)
                    ) : (
                      <Muted>-</Muted>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>
    </>
  );
};

export default SubscriptionsPage;
